import { faVimeo } from "@fortawesome/free-brands-svg-icons/faVimeo";
import { faYoutube } from "@fortawesome/free-brands-svg-icons/faYoutube";
import { faImage } from "@fortawesome/pro-regular-svg-icons/faImage";
import { faPenField } from "@fortawesome/pro-regular-svg-icons/faPenField";
import { faPlayCircle } from "@fortawesome/pro-regular-svg-icons/faPlayCircle";
import { faRotate } from "@fortawesome/pro-regular-svg-icons/faRotate";
import { faTrash } from "@fortawesome/pro-regular-svg-icons/faTrash";
import { faChevronLeft } from "@fortawesome/pro-solid-svg-icons/faChevronLeft";
import { faChevronRight } from "@fortawesome/pro-solid-svg-icons/faChevronRight";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Card, CardActions, CardMedia, Grid, Typography } from "@mui/material";
import { AppGlobalClassKeys } from "app/appGlobalStyles";
import { AppImageDimension, AppImages } from "app/appImages";
import { MprcClickable, MprcConfirmDialog, MprcContainer, MprcLink, MprcSpinnerIndicator, MprcText } from "components";
import { AsyncUploader } from "components/AsyncUploader";
import { CustomVideoSlideRender } from "components/CustomVideoSlideRender";
import { ImageSelectionResult, MprcImageSelectionDialog } from "components/MprcImageHandler";
import { onImageSelectionUploadError } from "components/MprcImageHandler/MprcImageSelectionDialog";
import { MprcSimpleEditDialog } from "components/MprcSimpleEditDialog";
import { AsyncUploadResult, GetPublicationAttachDto, GetPublicationPresentationDto, PublicationAttachType } from "generated/CoreApi_v1";
import MediaUtils from "lib/MediaUtils";
import { useSnackbar } from "notistack";
import * as React from 'react';
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Lightbox from "yet-another-react-lightbox";
import Captions from "yet-another-react-lightbox/plugins/captions";
import Fullscreen from "yet-another-react-lightbox/plugins/fullscreen";
import Zoom from "yet-another-react-lightbox/plugins/zoom";

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export type PublicationMediaViewData = {
  presentation?: GetPublicationPresentationDto | null;
  attachs: GetPublicationAttachDto[];
};
interface IProps {
  data: PublicationMediaViewData;
  onChange: (data: PublicationMediaViewData) => void;
  onUploading: (uploading: boolean) => void;
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////

const rotations = [0, 5, 10, 15, 20, 30, 45, 60];

const InternalPublicationMediaView = ({ data, onChange, onUploading }: IProps) => {
  const [dialog, setDialog] = useState<any>(null);
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const presentation = data.presentation!;
  const attachs = data.attachs;
  const total = presentation.orderedItems.length;

  ///
  const moveLeft = (index: number) => () => {
    if (index > 0) {
      var aux = presentation.orderedItems[index];
      presentation.orderedItems[index] = presentation.orderedItems[index - 1];
      presentation.orderedItems[index - 1] = aux;
      onChange({ presentation, attachs });
    }
  };

  ///
  const moveRight = (index: number) => () => {
    if (index < total - 1) {
      var aux = presentation.orderedItems[index];
      presentation.orderedItems[index] = presentation.orderedItems[index + 1];
      presentation.orderedItems[index + 1] = aux;
      onChange({ presentation, attachs });
    }
  };

  ///
  const handleRotation = () => {
    var index = rotations.findIndex(p => p == presentation.rotationInterval);
    if (index != -1) {
      if (index < rotations.length - 1) presentation.rotationInterval = rotations[index + 1];
      else presentation.rotationInterval = rotations[0];
      onChange({ presentation, attachs });
    }
  };

  ///
  const handleMediaViewer = (index: number) => () => {
    var id = presentation.orderedItems[index];
    var attach = attachs.find(p => p.attachId == id);
    if (attach != null) {
      setDialog(<Lightbox
        open
        close={() => setDialog(null)}
        slides={
          attach.file && attach.attachType == PublicationAttachType.Image
            ? [{
              type: "image",
              src: attach.file.url!,
              description: attach.caption,
              height: attach.file.metadata.height!,
              width: attach.file.metadata.width!,
            }]
            : attach.file
              ? [{
                type: "custom-video-slide",
                id: attach.attachId,
                videoId: attach.file.fileId!,
                url: attach.file.url!,
                description: attach.caption,
                height: attach.file.metadata.height!,
                width: attach.file.metadata.width!,
                duration: attach.file.metadata.duration ?? 0,
                mimetype: attach.file.mimetype,
                isAudio: attach.file.mimetype.indexOf("audio") > -1,
                isHls: false,
                autoPlay: true,
                mediaSessionTitle: attach.file.metadata.title,
                poster: attach.file.mimetype.indexOf("audio") > -1
                  ? attach.cardImage?.url ?? AppImages.AudioPoster
                  : attach.file.mimetype.indexOf("video") > -1
                    ? AppImages.VideoMp4Poster
                    : undefined,
                tracks: attach.file.mediaTracks ?? []
              }]
              : attach.linkMetadata
                ? [{
                  type: "custom-video-slide",
                  id: attach.attachId,
                  videoId: attach.linkMetadata.externalId!,
                  url: attach.linkMetadata.provider == "YouTube"
                    ? MediaUtils.buildYoutubeEmbedHtml(attach.linkMetadata, true)
                    : attach.linkMetadata.url!,
                  title: attach.caption,
                  height: attach.linkMetadata.height!,
                  width: attach.linkMetadata.width!,
                  duration: attach.linkMetadata.duration ?? 0,
                  isAudio: false,
                  isHls: false,
                  autoPlay: true,
                  mediaSessionTitle: attach.linkMetadata.title,
                  poster: attach.linkMetadata.thumbnailUrl ?? undefined
                }]
                : []
        }
        render={
          {
            slide: ({ slide, offset }) =>
              "type" in slide && slide.type === "custom-video-slide"
                ? <CustomVideoSlideRender slide={slide} offset={offset} />
                : null,

            buttonPrev: () => null,
            buttonNext: () => null
          }
        }
        zoom={{
          maxZoomPixelRatio: 1,
          zoomInMultiplier: 2,
          doubleTapDelay: 300,
          doubleClickDelay: 300,
          doubleClickMaxStops: 2,
          keyboardMoveDistance: 50,
          wheelZoomDistanceFactor: 100,
          pinchZoomDistanceFactor: 100,
          scrollToZoom: true
        }}
        plugins={[Captions, Fullscreen, Zoom]}
      />);
    }
  };

  ///
  const removeFromPresentation = (index: number) => {
    var id = presentation.orderedItems[index];
    var newPresentation = { ...presentation, orderedItems: presentation.orderedItems.filter((_, i) => i != index) };
    var newAttachs = attachs.filter(p => p.attachId != id);
    onChange({ presentation: newPresentation, attachs: newAttachs });
  };

  ///
  const handleDelete = (index: number) => () => {
    var id = presentation.orderedItems[index];
    var fileToDelete = attachs.find(p => p.attachId == id);

    if (fileToDelete != null) {
      setDialog(
        <MprcConfirmDialog
          open
          message={t("AsyncUpload.ConfirmRemoveUploadText", { ns: "shared" })}
          onClose={
            (result) => {
              setDialog(null);
              if (result) {
                removeFromPresentation(index);
              }
            }
          }
        />);
    }
    else {
      removeFromPresentation(index);
    }
  };

  ///////////////////////////////////////////////////////////////////////////

  const [loadingThumbIndex, setLoadingThumbIndex] = useState<string | undefined>(undefined);

  ///
  const loadAttachSuccess = (attach: GetPublicationAttachDto, data: AsyncUploadResult) => {
    if (data.error) {
      enqueueSnackbar(t("AsyncUpload.UploadProccessFailed", { ns: "shared" }), { variant: "error" });
    }
    else {
      attach.cardImage = data.mainFile;
      attach.thumbnail = data.thumbnail;
      onChange({ presentation, attachs });
    }
  };

  ///
  const doLoadImage = (attach: GetPublicationAttachDto, image: ImageSelectionResult | null) => {
    if (image != null) {

      const asyncUploader = new AsyncUploader({
        onFileUploadSuccess: (data) => loadAttachSuccess(attach, data),
        onFileUploadError: (inst, error) => onImageSelectionUploadError(inst, error, t),
        onFileFinished: () => { setLoadingThumbIndex(undefined); onUploading(false); },
        onFileStarted: () => { setLoadingThumbIndex(attach.attachId); onUploading(true); }
      });

      var finalImage = image.editedImage ?? image.source;
      asyncUploader.uploadImage(finalImage, true);
    }

    setDialog(null);
  };

  ///
  const handleThumbnail = (index: number) => () => {
    var id = presentation.orderedItems[index];
    var attach = attachs.find(p => p.attachId == id);
    if (!attach) return;

    setDialog(<MprcImageSelectionDialog
      acceptedFiles={MediaUtils.DropzoneAcceptImages()}
      initialSource="library"
      backgroundType="solid"
      allowEdit={true}
      dimension={AppImageDimension.CardPicture}
      onClose={(image) => doLoadImage(attach!, image)}
    />);
  };

  ///////////////////////////////////////////////////////////////////////////

  ///
  const handleCaption = (index: number) => () => {
    var id = presentation.orderedItems[index];
    var attach = attachs.find(p => p.attachId == id);
    if (attach == null) return;

    setDialog(<MprcSimpleEditDialog
      title="Editar Legenda"
      value={attach.caption ?? ""}
      acceptEmptyValue
      onConfirm={
        async (value) => {
          attach!.caption = value ?? null;
          onChange({ presentation, attachs });
          setDialog(null);
        }
      }
      onClose={() => setDialog(null)}
    />);
  };

  ///
  const memoizedRender = useMemo(() => {
    return (
      <>
        {
          total > 1 &&
          <Grid container justifyContent="space-between" alignItems="center">
            <Grid>
              <Typography variant="h4">{t("AsyncUpload.PresentationText", { ns: "shared" })}</Typography>
            </Grid>
            <Grid>
              <MprcClickable onClick={handleRotation}>
                <FontAwesomeIcon icon={faRotate} fixedWidth />
                {
                  presentation.rotationInterval == 0
                    ? t("AsyncUpload.ManualRotationText", { ns: "shared" })
                    : presentation.rotationInterval + "s"
                }
              </MprcClickable>
            </Grid>
            <Grid>
              {total} itens
            </Grid>
          </Grid>
        }

        <Box
          sx={(theme) => ({
            display: "flex",
            flexWrap: "nowrap",
            alignItems: "center",
            paddingTop: theme.spacing(1),
            paddingBottom: theme.spacing(1),
            marginTop: theme.spacing(2),
            marginBottom: theme.spacing(2),
            overflowX: "auto",
          })}
          className={AppGlobalClassKeys.thinScrollbar}
        >
          {
            presentation.orderedItems.map((id, index) => {
              var url = ""
                , altText = ""
                , canEditCaption = false
                , canEditThumbnail = false
                , videoProviderImg: React.ReactNode = null;

              var attach = attachs.find(p => p.attachId == id);
              if (attach != null && attach.file != null) {
                url = attach.thumbnail?.url ?? attach.file.url ?? "";
                altText = attach.file.metadata.title ?? attach.file.metadata.filename;
                canEditCaption = attach?.attachType == PublicationAttachType.Image;
                canEditThumbnail = attach?.attachType == PublicationAttachType.Audio;

                if (attach.attachType == PublicationAttachType.Video) {
                  if (!attach.videoProcessed) {
                    url = attach.file.mimetype == "video/mp4"
                      ? AppImages.VideoMp4Thumbnail
                      : AppImages.VideoProcessingThumbnail;
                  }
                  if (attach.file.mimetype == "video/mp4")
                    videoProviderImg = <FontAwesomeIcon icon={faPlayCircle} size="3x" color="white" />;
                }
                else if (attach.attachType == PublicationAttachType.Audio) {
                  url = attach.thumbnail ? attach.thumbnail.url : AppImages.AudioThumbnail;
                  videoProviderImg = <FontAwesomeIcon icon={faPlayCircle} size="3x" color="white" />;
                }
              }
              else if (attach?.linkMetadata != null) {
                url = attach.linkMetadata.thumbnailUrl ?? attach.thumbnail?.url ?? "";
                altText = attach.linkMetadata.title;
                videoProviderImg = attach.linkMetadata.provider == "YouTube"
                  ? <FontAwesomeIcon icon={faYoutube} size="3x" color="red" />
                  : attach.linkMetadata.provider == "Vimeo"
                    ? <FontAwesomeIcon icon={faVimeo} size="3x" color="white" />
                    : null;
              }

              return url == ""
                ? null
                : (
                  <Card sx={{ width: AppImageDimension.ThumbnailWide.width, maxWidth: AppImageDimension.ThumbnailWide.width, ml: 1, flexShrink: 0 }}>
                    <div style={{ position: "relative" }}>
                      <MprcClickable onClick={handleMediaViewer(index)}>
                        <CardMedia
                          component="img"
                          width={AppImageDimension.ThumbnailWide.width}
                          height={AppImageDimension.ThumbnailWide.height}
                          image={url}
                          alt={altText}
                        />
                        {
                          videoProviderImg &&
                          <div className={AppGlobalClassKeys.centerChild}>
                            {videoProviderImg}
                          </div>
                        }
                      </MprcClickable>
                    </div>
                    <CardActions sx={{ justifyContent: "space-between", alignItems: "center" }}>
                      <div>
                        {
                          canEditCaption &&
                          <MprcClickable onClick={handleCaption(index)}>
                            <MprcText color={attach?.caption ? "warning" : "inherit"}>
                              <FontAwesomeIcon icon={faPenField} fixedWidth />
                            </MprcText>
                          </MprcClickable>
                        }
                        {
                          canEditThumbnail
                            ? loadingThumbIndex && loadingThumbIndex === attach?.attachId
                              ? <MprcSpinnerIndicator />
                              : <MprcClickable onClick={handleThumbnail(index)}>
                                <MprcText color={attach?.thumbnail ? "warning" : "inherit"}>
                                  <FontAwesomeIcon icon={faImage} fixedWidth />
                                </MprcText>

                              </MprcClickable>
                            : null
                        }
                      </div>
                      {
                        total > 1 &&
                        <div>
                          {index > 0 && index < total && <MprcLink onClick={moveLeft(index)}><FontAwesomeIcon icon={faChevronLeft} fixedWidth /></MprcLink>}
                          <span>{index + 1}</span>
                          {index < total - 1 && <MprcLink onClick={moveRight(index)}><FontAwesomeIcon icon={faChevronRight} fixedWidth /></MprcLink>}
                        </div>
                      }
                      <div>
                        <MprcClickable onClick={handleDelete(index)}>
                          <MprcText color="error"><FontAwesomeIcon icon={faTrash} fixedWidth /></MprcText>
                        </MprcClickable>
                      </div>
                    </CardActions>
                  </Card>
                );
            })
          }
        </Box>
      </>
    );
  }, [data, loadingThumbIndex]);

  ////////////////////////////////////////////////
  /// RENDER
  ////////////////////////////////////////////////
  return (
    <MprcContainer>
      {dialog}
      {memoizedRender}
    </MprcContainer>
  );
};

export const PublicationMediaView: React.FC<IProps> = (props) => {
  return props.data.presentation == null || props.data.presentation.orderedItems.length == 0
    ? null
    : <InternalPublicationMediaView {...props} />;
};
